"use client";

import { type FC, Fragment, type ReactNode } from "react";
import { mergeClasses } from "@huub-web/utils";
import { Flex, LogoSmall, Ruler, StepNavigationItem } from "@huub-web/ui";
import { useNavigationState } from "../../providers";
import type { ThemeName } from "@huub-web/ui/theme/types";

import {
  logoHoverInvertStyle,
  menuInnerStyle,
  menuItemsStyle,
  menuItemsWrapperStyle,
  menuNavStyle,
  rulerStyle,
  trailingMenuItemsWrapperStyle,
} from "./NavigationMenu.css";

type NavigationMenuProps = {
  /**
   * Content rendered below the main menu items (avatar popup, logout button, etc.)
   */
  trailing?: ReactNode;
  /**
   * Layout variant: "default" for desktop sidebar, "full" for mobile expanded menu
   */
  variant?: "default" | "full";
  /**
   * Whether navigation items are disabled
   */
  disabled?: boolean;
  /**
   * Active theme name, used for branded logo rendering
   */
  theme?: ThemeName;
  /**
   * Whether to hide the logo on the hub item
   */
  hideLogo?: boolean;
  /**
   * Called when a main navigation item is clicked
   */
  onNavigationChange?: (key: string) => void;
};

/**
 * Shared menu items list used by both DesktopNavigation and MobileNavigation.
 * Renders the main navigation items and trailing items.
 */
export const NavigationMenu: FC<NavigationMenuProps> = ({
  trailing,
  variant = "default",
  disabled = false,
  theme = "light",
  hideLogo = false,
  onNavigationChange,
}) => {
  const { navigationState, navigationContext, menuVariant, getNavigationPath } =
    useNavigationState();

  const mainMenuItems = navigationState.filter(
    (step) => !step.hidden && step.position !== "trailing",
  );

  const trailingMenuItems = navigationState.filter(
    (step) => !step.hidden && step.position === "trailing",
  );

  const isFullVariant = variant === "full";

  return (
    <Flex className={menuInnerStyle({ variant })}>
      <nav className={menuNavStyle({ variant })}>
        <div
          className={mergeClasses(
            menuItemsStyle,
            menuItemsWrapperStyle({ variant }),
          )}
        >
          {mainMenuItems.map((step, i) => (
            <Fragment key={step.id}>
              {step.ruler && <Ruler className={rulerStyle} variant="border" />}
              <StepNavigationItem
                id={step.id}
                active={step.id === navigationContext?.[0]}
                onClick={() => onNavigationChange?.(step.id)}
                href={getNavigationPath(step.id) ?? undefined}
                step={step}
                disabled={disabled || step.disabled}
                {...(isFullVariant
                  ? { appearance: "rounded", variant: "full" }
                  : {})}
              >
                {step.id === "hub" &&
                  !hideLogo &&
                  !["light", "dark"].includes(theme as string) && (
                    <LogoSmall
                      theme={theme}
                      className={
                        theme === "eviVanLanschot" ||
                        theme === "allianz" ||
                        theme === "nvm" ||
                        theme === "achmea" ||
                        theme === "domek"
                          ? logoHoverInvertStyle
                          : undefined
                      }
                    />
                  )}
              </StepNavigationItem>
              {menuVariant === "default" && i === 0 && (
                <Ruler className={rulerStyle} variant="border" />
              )}
            </Fragment>
          ))}
        </div>

        <div className={trailingMenuItemsWrapperStyle}>
          {trailingMenuItems.map((step) => (
            <StepNavigationItem
              key={step.id}
              id={step.id}
              active={step.id === navigationContext?.[0]}
              onClick={() => onNavigationChange?.(step.id)}
              href={getNavigationPath(step.id) ?? undefined}
              step={step}
              disabled={disabled || step.disabled}
              {...(isFullVariant
                ? { appearance: "rounded", variant: "full" }
                : {})}
            />
          ))}
          {trailing}
        </div>
      </nav>
    </Flex>
  );
};
